import {
  autoUpdate,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import {
  AlertCircle,
  Building2,
  ChevronDown,
  ExternalLink,
  Link2,
  Loader2,
  Pencil,
  Send,
  Unlink,
  X,
  Zap,
} from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CrashingOutRunDetailsPanel } from '@/components/agentflow/CrashingOutRunDetailsPanel';
import { RecordsRequestAgentButton } from '@/components/agentflow/RecordsRequestAgentButton';
import {
  buildClaimsFilingUrl,
  buildCoverageOverviewUrl,
  resolvePremisesLorStep,
} from '@/components/claims-filing/buildClaimsFilingUrl';
import { buildPremisesFollowUpUrlForType } from '@/components/claims-filing/premises/premisesFlow';
import { useCoverageStepProgress } from '@/components/claims-filing/state/useCoverageStepProgress';
import { ReviewGroupCoveragesModal } from '@/components/coverage/companion/ReviewGroupCoveragesModal';
import { CoverageCardRedesign } from '@/components/coverage/shared/CoverageCardRedesign';
import { DeliverablesButton } from '@/components/deliverables';
import { useUploadManagerUnified } from '@/components/documents-v2/hooks/useUploadManagerUnified';
import { InitialReviewModal } from '@/components/initial-review';
import { PoliceOverviewMainContent } from '@/components/police/PoliceOverviewMainContent';
import { PropertyLookupDrawer } from '@/components/property-lookup/PropertyLookupDrawer';
import { TaskCSATSurvey } from '@/components/tasks/TaskCSATSurvey';
import { Verify1PCoverageModal } from '@/components/tasks/verify-1p-coverage';
import { InteractiveRequestStatusBadge } from '@/components/treatment/v3/request/badges/RequestStatusBadge';
import { RequestPageModal } from '@/components/treatment/v3/request/modals/RequestPageModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  NonModalRightDialogContent,
} from '@/components/ui/dialog';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  INITIAL_REVIEW_EVENTS,
  INITIAL_REVIEW_PROPERTIES,
} from '@/constants/analytics/initial-review-events';
import { POSTHOG_SURVEYS } from '@/constants/analytics/posthog-surveys';
import {
  buildTreatmentCheckInComment,
  requiresDueDateChangeReason,
  requiresStatusChangeReason,
  TREATMENT_CHECK_IN_CHECKBOXES,
  TREATMENT_CHECK_IN_PROTECTED_STATUSES,
  TREATMENT_CHECK_IN_STATUS_WARNING,
} from '@/constants/task-field-change-reasons';
import {
  FieldChangeReasonProvider,
  useFieldChangeReason,
} from '@/contexts/FieldChangeReasonContext';
import { usePushChanges } from '@/contexts/PushChangesContext';
import { usePromptPushNote } from '@/contexts/PushNoteToCmsContext';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { useTaskEffortTracking } from '@/hooks/useTaskEffortTracking';
import { filterStaffUsers } from '@/lib/utils';
import { MatterProvider } from '@/providers/MatterProvider';
import {
  useBulkUpdateTasksMutation,
  useCreateTaskUpdateMutation,
  useDeleteTaskMutation,
  useDeleteTaskUpdateMutation,
  useGetCollectionTreeQuery,
  useGetCoveragesQuery,
  useGetTaskQuery,
  useGetTasksQuery,
  useGetTaskStatusesQuery,
  useGetTaskUpdatesQuery,
  useUnwatchTaskMutation,
  useUpdateTaskExternalCMSStatusMutation,
  useUpdateTaskMutation,
  useUpdateTaskUpdateMutation,
  useWatchTaskMutation,
} from '@/services/api';
import { useStartRecordsRequestEvaluationMutation } from '@/services/api/agentflowService';
import { useCorrespondencePackageListQuery } from '@/services/api/correspondenceService';
import {
  useGetSourceActionsQuery,
  useResolveExternalNoteQuery,
} from '@/services/api/externalNoteService';
import { enhancedApi as deliverablesApi } from '@/services/api/generated/deliverablesServicev2';
import {
  enhancedApi as externalTaskApi,
  type ExternalTaskWithLinkSchema,
} from '@/services/api/generated/externalTaskService';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { useGetMedicalProviderQuery } from '@/services/api/treatmentService';
import { useGetStaffUsersQuery } from '@/services/api/userService';
import {
  PhoneCallAction,
  Task,
  TaskUpdate,
  TaskUpdateVisibility,
} from '@/services/types/client-intake-types';
import {
  CoverageCategory,
  CoverageListItem,
  CoverageSchema,
  CoverageStatus,
  CoverageStub,
} from '@/services/types/coverage-types';
import {
  CanonicalTaskGroup,
  CanonicalTaskStatus,
  CanonicalTaskType,
  TASK_TEMPLATE_TYPES,
} from '@/services/types/task-types';
import { selectUser } from '@/state/selectors/root-selectors';
import { canShowCSATSurvey, markCSATSurveyShown } from '@/utils/csatCooldown';
import { createConsistentDate } from '@/utils/date';
import { filterTaskStatusesByGroup } from '@/utils/taskStatusUtils';

import { OperatorMessageModal } from '../OperatorMessageModal';
import { TaskStatusBadge } from '../TaskStatusBadge';
import { VoiceAICallModal } from '../VoiceAICallModal';
import { AIHoldAndTransferCard } from './AIHoldAndTransferCard';
import { CompanionTasksTab } from './CompanionTasksTab';
import { DocumentSelectionModal } from './DocumentSelectionModal';
import { ExternalTaskLinkingInterface } from './ExternalTaskLinkingInterface';
import { PoliceReportContext } from './PoliceReportContext';
import { ReassignmentDateBumpNotice } from './ReassignmentDateBumpNotice';
import {
  getTaskActionConfig,
  isSubmitRequestTask,
  TaskActionButton,
} from './TaskActionButton';
import { TaskActionModal } from './TaskActionModal';
import { TaskAttachments } from './TaskAttachments';
import { TaskDescription } from './TaskDescription';
import { TaskGuidelines } from './TaskGuidelines';
import { TaskHeader } from './TaskHeader';
import { TaskInstructionCard } from './TaskInstructionCard';
import { TaskInstructionDebugButton } from './TaskInstructionDebugButton';
import { TaskInternalNotes } from './TaskInternalNotes';
import { TaskMetadata } from './TaskMetadata';
import { TaskSection } from './TaskSection';
import { RelatedActivityTimeline, Timeline } from './timeline';
import { isUserNote } from './timeline/types';
import { TreatmentRequestEntrySheet } from './TreatmentRequestEntrySheet';
import { UpworkEscalationStatusLine } from './UpworkEscalationStatusLine';
import { computeReassignmentDateBump } from './utils/reassignmentDateBump';

interface TaskDetailProps {
  taskId: string;
  matterId?: string;
  onClose: () => void;
  isModal?: boolean;
  container?: HTMLElement | null;
  /** Optional: Pass task data directly to avoid refetching (optimizes performance) */
  task?: Task;
  /** When true, hides status badge and Mark Complete button in TaskHeader (used when these controls are in the panel header) */
  hideStatusControls?: boolean;
  /** Mutable ref that will be populated with TaskDetail's internal handleStatusChange handler.
   *  This allows parent components (e.g. MatterLayout panel header) to delegate status changes
   *  to TaskDetail's full validation logic (change reasons, TRR checks, external sync, etc.) */
  onStatusChangeRef?: React.MutableRefObject<((status: { id: string; canonical_status: string }) => void | Promise<void>) | null>;
}

const AUTO_LINKABLE_COVERAGE_TASK_TYPES = new Set<CanonicalTaskType>([
  CanonicalTaskType.OPEN_CLAIM,
  CanonicalTaskType.SEND_LOR,
  CanonicalTaskType.LOR_ACKNOWLEDGEMENT,
  CanonicalTaskType.DECLARATION_PAGE,
  CanonicalTaskType.GET_LIABILITY_LIMITS,
]);

/**
 * PROPERTY coverage id to use for premises URLs (`/premises/:id/confirm-location`, stepper).
 * Review Incident Properties is matter-level; task.coverage_id is often unset or unrelated.
 */
function getPremisesReviewStepperCoverageId(
  coverages: CoverageListItem[] | undefined,
): string | undefined {
  if (!coverages?.length) return undefined;
  const propertyCoverages = coverages.filter(
    (c) => c.category === CoverageCategory.PROPERTY,
  );
  if (propertyCoverages.length === 0) return undefined;

  const statusRank = (status: CoverageStatus | null | undefined): number => {
    if (status === CoverageStatus.NEEDS_APPROVAL) return 0;
    if (status === CoverageStatus.APPROVED) return 1;
    return 2;
  };

  const sorted = [...propertyCoverages].sort((a, b) => {
    const dr = statusRank(a.status) - statusRank(b.status);
    if (dr !== 0) return dr;
    const ta = new Date((a as { created_at?: string | null }).created_at || 0).getTime();
    const tb = new Date((b as { created_at?: string | null }).created_at || 0).getTime();
    return tb - ta;
  });

  return sorted[0]?.id;
}

function buildTaskCompleteContext(
  task: Task,
  updates: TaskUpdate[],
  changeReason?: string,
): string {
  const parts: string[] = [];
  parts.push(`Task: ${task.name}`);
  if (task.assignee) {
    parts.push(`Assignee: ${task.assignee.first_name} ${task.assignee.last_name}`);
  }
  if (changeReason) {
    parts.push(`Completion note: ${changeReason}`);
  }
  const comments = updates
    .filter((u) => u.update?.trim())
    .map((u) => {
      const author =
        typeof u.user === 'object' ? u.user?.username : u.user;
      return `[${u.created_at}] ${author ?? 'System'}: ${u.update}`;
    });
  if (comments.length > 0) {
    parts.push('Task updates:');
    parts.push(...comments);
  }
  return parts.join('\n');
}

const TaskDetailContent: React.FC<TaskDetailProps> = ({
  taskId,
  matterId: propMatterId,
  onClose,
  isModal = true,
  container,
  task: propTask,
  hideStatusControls = false,
  onStatusChangeRef,
}) => {
  const params = useParams();
  const urlMatterId = params.matter_id;
  // Use matterId from props if provided, otherwise use from URL params
  const matterId = propMatterId || urlMatterId || propTask?.matter;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Only fetch task if not provided via props (optimization for parent-controlled views)
  const {
    data: fetchedTask,
    isLoading: isTaskLoading,
    error: taskError,
  } = useGetTaskQuery(
    { taskId, matterId: matterId! },
    {
      skip: !matterId,
      refetchOnMountOrArgChange: true,
    },
  );

  // Prefer fresh detail payload over the list-row snapshot passed by the drawer opener.
  const task = fetchedTask || propTask;
  const taskCanonicalType = task?.type?.canonical_type as
    | CanonicalTaskType
    | undefined;
  const { data: taskStatuses } = useGetTaskStatusesQuery();

  const completedTaskStatus = React.useMemo(() => {
    if (!taskStatuses || !task) {
      return null;
    }

    const filteredStatuses = filterTaskStatusesByGroup(taskStatuses, task);

    return (
      filteredStatuses.find(
        (status) =>
          status.canonical_status === CanonicalTaskStatus.COMPLETED &&
          (status.name.toLowerCase() === 'completed' ||
            status.name.toLowerCase() === 'complete'),
      ) ||
      filteredStatuses.find(
        (status) => status.canonical_status === CanonicalTaskStatus.COMPLETED,
      ) ||
      null
    );
  }, [task, taskStatuses]);

  // Effort Score tracking - emits task_viewed on mount, provides trackStatusUpdate for status changes
  const { trackStatusUpdate } = useTaskEffortTracking(task);

  // PostHog analytics
  const capture = usePostHogCapture();

  // Task CSAT survey state
  const [showCSATSurvey, setShowCSATSurvey] = React.useState(false);

  // Current user (for same-user filtering of recent updates)
  const currentUser = useSelector(selectUser);

  // Records Request Evaluation
  const [startEval, { isLoading: isEvalStarting }] =
    useStartRecordsRequestEvaluationMutation();


  // Global field change reason modal
  // isPending prevents drawer from closing while modal is open (race condition fix)
  const { requestChangeReason, isPending: isReasonModalPending } =
    useFieldChangeReason();

  // Fetch matter details
  const { data: matterDetails } = useGetMatterDetailsQuery(matterId!, {
    skip: !matterId,
  });

  // CMS resolution + push-note context for post-completion flow
  const { data: cmsResolution } = useResolveExternalNoteQuery(
    { matterId: matterId! },
    { skip: !matterId },
  );
  const { promptPushNote } = usePromptPushNote();

  // Fetch CMS push status for this task
  const { data: taskPushActions } = useGetSourceActionsQuery(
    { matterId: matterId!, sourceType: 'TASK_COMPLETE', sourceIds: [taskId] },
    {
      skip: !matterId || !cmsResolution?.cms_type,
      pollingInterval: 5000,
      skipPollingIfUnfocused: true,
    },
  );
  const taskPushAction = taskPushActions?.[taskId];


  const [updateTask] = useUpdateTaskMutation();
  const [watchTask] = useWatchTaskMutation();
  const [unwatchTask] = useUnwatchTaskMutation();
  const [optimisticWatched, setOptimisticWatched] = React.useState<boolean | null>(null);
  const isWatched = optimisticWatched ?? task?.is_watched ?? false;
  const [bulkUpdateTasks] = useBulkUpdateTasksMutation();
  const [updateTaskUpdate] = useUpdateTaskUpdateMutation();
  const [deleteTaskUpdate] = useDeleteTaskUpdateMutation();
  const [createTaskUpdate] = useCreateTaskUpdateMutation();
  const [deleteTask, { isLoading: isDeleting }] = useDeleteTaskMutation();
  const [updateTaskCMSStatus] = useUpdateTaskExternalCMSStatusMutation();
  const { data: coverages, isLoading: isCoveragesLoading } =
    useGetCoveragesQuery(
      { matterId: matterId!, includeShared: true },
      { skip: !matterId },
    );
  const coverageOptions = React.useMemo<CoverageStub[]>(() => {
    if (!coverages) return [];
    return coverages.map((coverage) => ({
      id: coverage.id,
      carrier: coverage.carrier,
      category: coverage.category,
      policy_identifiers: coverage.policy_identifiers,
      named_insured: coverage.named_insured,
      policy_type: coverage.policy_type,
    }));
  }, [coverages]);
  const premisesReviewStepperCoverageId = React.useMemo(
    () => getPremisesReviewStepperCoverageId(coverages),
    [coverages],
  );
  const linkedCoverageIdForNavigation =
    (task?.coverage?.id as string | undefined) ||
    (task?.coverage_id as string | undefined);
  const linkedCoverageForNavigation = coverages?.find(
    (coverage) => coverage.id === linkedCoverageIdForNavigation,
  );
  const { sendLorPremisesDone, reviewSendPremisesDone } =
    useCoverageStepProgress(matterId ?? '', linkedCoverageIdForNavigation ?? '');
  const {
    data: premisesLorPackages,
  } = useCorrespondencePackageListQuery(
    {
      matterId: matterId ?? '',
      packageType: 'premises_lor',
    },
    {
      skip:
        !matterId ||
        !linkedCoverageIdForNavigation ||
        taskCanonicalType !== CanonicalTaskType.OPEN_PREMISES_CLAIM,
    },
  );
  const [isUpdatingCoverageLink, setIsUpdatingCoverageLink] =
    React.useState(false);
  const [isLinkCoverageDialogOpen, setIsLinkCoverageDialogOpen] =
    React.useState(false);
  const [selectedCoverageForLink, setSelectedCoverageForLink] = React.useState<
    string | null
  >(null);
  const [internalNotes, setInternalNotes] = React.useState<string | null>(null);
  const preEditDescriptionRef = React.useRef<string>('');
  const { data: taskUpdates, refetch: refetchTaskUpdates } =
    useGetTaskUpdatesQuery(
      { matterId: matterId!, taskId },
      { skip: !matterId },
    );
  // Find the most recent user-posted update (by the current user) to suggest in the reason modal.
  // Time filtering (5 min) is done at render time in the modal to avoid stale memoized values.
  const recentUserUpdate = React.useMemo(() => {
    if (!taskUpdates || taskUpdates.length === 0 || !currentUser) return undefined;

    const recent = taskUpdates
      .filter((update) => {
        if (!isUserNote(update)) return false;
        if (update.correlation_id) return false;
        if (!update.update.trim()) return false;
        // Only suggest updates posted by the current user
        const updateUser = update.user;
        if (typeof updateUser === 'object' && updateUser !== null) {
          if (updateUser.id !== currentUser.id) return false;
        } else if (typeof updateUser === 'string') {
          if (updateUser !== currentUser.id && updateUser !== currentUser.email) return false;
        } else {
          return false;
        }
        return true;
      })
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

    return recent.length > 0
      ? { text: recent[0].update, createdAt: recent[0].created_at }
      : undefined;
  }, [taskUpdates, currentUser]);

  const { data: staffUsers, isLoading: isStaffUsersLoading } =
    useGetStaffUsersQuery();
  const filteredStaffUsers = React.useMemo(() => {
    return filterStaffUsers(staffUsers || []);
  }, [staffUsers]);

  // Check if current task is in INITIAL_REVIEW group (use task.group which is the canonical source)
  const isInitialReviewTask =
    task?.group?.canonical_group === CanonicalTaskGroup.INITIAL_REVIEW;

  const canonicalTaskType =
    typeof task?.type === 'object'
      ? (task.type?.canonical_type as CanonicalTaskType | undefined)
      : undefined;

  const explicitCoverageId =
    (task?.coverage?.id as string | undefined) ||
    (task?.coverage_id as string | undefined) ||
    undefined;

  const inferredCoverage = React.useMemo(() => {
    if (
      explicitCoverageId ||
      !coverages ||
      !canonicalTaskType ||
      !AUTO_LINKABLE_COVERAGE_TASK_TYPES.has(canonicalTaskType)
    ) {
      return null;
    }

    let allowedCategories: CoverageCategory[] = [];
    if (task?.group?.canonical_group === CanonicalTaskGroup.FIRST_PARTY_INSURANCE) {
      allowedCategories = [CoverageCategory.FIRST_PARTY];
    } else if (task?.group?.canonical_group === CanonicalTaskGroup.THIRD_PARTY_INSURANCE) {
      allowedCategories = [CoverageCategory.THIRD_PARTY, CoverageCategory.PROPERTY];
    } else {
      return null;
    }

    const eligibleCoverages = coverages.filter(
      (coverage) =>
        coverage.status === CoverageStatus.APPROVED &&
        allowedCategories.includes(coverage.category as CoverageCategory),
    );

    return eligibleCoverages.length === 1 ? eligibleCoverages[0] : null;
  }, [canonicalTaskType, coverages, explicitCoverageId, task?.group?.canonical_group]);

  const linkedCoverageId = explicitCoverageId || inferredCoverage?.id || undefined;
  const linkedCoverage = React.useMemo(() => {
    // Prefer full coverage data from the coverages endpoint over the task's
    // embedded stub (CoverageStub) which only contains id, category, carrier,
    // policy_identifiers, named_insured, and policy_type — missing benefits,
    // adjusters, claims, status, liability_status, policy_details, etc.
    if (linkedCoverageId && coverages) {
      const fullCoverage = coverages.find((coverage) => coverage.id === linkedCoverageId) as CoverageListItem | undefined;
      if (fullCoverage) return fullCoverage;
    }
    if (task?.coverage) {
      return task.coverage as unknown as CoverageListItem;
    }
    return null;
  }, [coverages, linkedCoverageId, task?.coverage]);

  const taskWithResolvedCoverage = React.useMemo(() => {
    if (!task || (!linkedCoverageId && !linkedCoverage)) {
      return task;
    }

    const resolvedCoverage =
      (linkedCoverage as unknown as Task['coverage'] | null) ?? task.coverage ?? undefined;

    if (task.coverage_id === linkedCoverageId && task.coverage === resolvedCoverage) {
      return task;
    }

    return {
      ...task,
      coverage_id: linkedCoverageId ?? task.coverage_id,
      coverage: resolvedCoverage,
    };
  }, [linkedCoverage, linkedCoverageId, task]);

  const autoLinkedTaskRef = React.useRef<string | null>(null);

  const showAgentRunDetails = Boolean(matterId && linkedCoverageId);

  const agentStatusLabel = (task?.crashing_out_agent_status || 'not_running').replace(/_/g, ' ');

  // Check if current task is a Treatment Check-In task
  const isTreatmentCheckInTask = React.useMemo(() => {
    if (!task) return false;

    const canonicalType =
      typeof task.type === 'object'
        ? (task.type?.canonical_type as CanonicalTaskType | undefined)
        : undefined;
    const taskTypeName =
      typeof task.type === 'string'
        ? task.type
        : (task.type?.name ?? task.name);
    const canonicalTypeLabel =
      typeof task.type === 'string' ? task.type : task.type?.canonical_type;
    const normalizeTaskTypeLabel = (value?: string | null) =>
      (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

    // If there's a specific canonical type that isn't TREATMENT_CHECK_IN,
    // respect it and don't override with name-based detection
    if (
      canonicalType &&
      canonicalType !== CanonicalTaskType.TREATMENT_CHECK_IN
    ) {
      return false;
    }

    // Explicit canonical type match
    if (canonicalType === CanonicalTaskType.TREATMENT_CHECK_IN) {
      return true;
    }

    // Fallback to name-based detection only when no canonical type is set
    return (
      normalizeTaskTypeLabel(taskTypeName).includes('treatmentcheckin') ||
      normalizeTaskTypeLabel(canonicalTypeLabel).includes('treatmentcheckin') ||
      (taskTypeName &&
        typeof taskTypeName === 'string' &&
        taskTypeName.toLowerCase().includes('treatment check')) ||
      (task.name && task.name.toLowerCase().includes('treatment check'))
    );
  }, [task]);

  // Fetch other Initial Review tasks for bulk update (only when viewing an Initial Review task)
  // Scoped to current matter only for security
  const { data: otherInitialReviewTasks } = useGetTasksQuery(
    {
      matter: matterId,
      canonical_groups: [CanonicalTaskGroup.INITIAL_REVIEW],
      statuses: [
        CanonicalTaskStatus.PENDING,
        CanonicalTaskStatus.BLOCKED,
        CanonicalTaskStatus.IN_PROGRESS,
      ],
      limit: 50,
    },
    {
      skip: !isInitialReviewTask || !matterId,
    },
  );

  // Filter out the current task and map to BulkUpdateTask format
  const bulkUpdateInitialReviewTasks = React.useMemo(() => {
    if (!otherInitialReviewTasks?.results || !taskId) return [];
    return otherInitialReviewTasks.results
      .filter((t) => t.id !== taskId)
      .map((t) => ({
        id: t.id,
        name: t.name,
        matter: t.matter,
        due_date: t.due_date,
      }));
  }, [otherInitialReviewTasks?.results, taskId]);

  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = React.useState(false);
  const [showVoiceAIModal, setShowVoiceAIModal] = React.useState(false);

  // TRR attachment requirement state: holds the pending status change while waiting for file upload
  const [trrAttachmentModalOpen, setTrrAttachmentModalOpen] =
    React.useState(false);
  const pendingTrrStatusRef = React.useRef<{
    status: { id: string; canonical_status: string };
    changeReason?: string;
  } | null>(null);

  // Fetch root collection for drag-and-drop uploads
  const { data: collectionTree } = useGetCollectionTreeQuery(matterId ?? '', {
    skip: !matterId,
  });
  const rootCollectionId = collectionTree?.id;

  // Upload manager for TRR attachment modal and drag-and-drop uploads
  const { handleUploadToCollection: handleTrrUpload, handleUploadToCollectionAndReturn } =
    useUploadManagerUnified({
      matterId: matterId ?? '',
      refetch: () => {},
    });

  // Drag-and-drop state for the entire task panel
  const [isDraggingOver, setIsDraggingOver] = React.useState(false);
  const dragCounterRef = React.useRef(0);

  const handlePanelDragEnter = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingOver(true);
    }
  }, []);

  const handlePanelDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDraggingOver(false);
    }
  }, []);

  const handlePanelDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handlePanelDrop = React.useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDraggingOver(false);
      dragCounterRef.current = 0;

      if (!matterId || !task || !rootCollectionId) return;

      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;

      try {
        // Upload to the matter's root collection and get blob objects back
        const blobs = await handleUploadToCollectionAndReturn(files, rootCollectionId);
        if (blobs && blobs.length > 0) {
          // Attach uploaded blobs to the task
          const existingAttachmentIds = task.attachments?.map((a: { id: string }) => a.id) ?? [];
          const newBlobIds = blobs.map((b) => b.id);
          await updateTask({
            matterId,
            taskId,
            task: {
              attachments: [...existingAttachmentIds, ...newBlobIds],
            },
          });
          toast.success(`${blobs.length} file${blobs.length > 1 ? 's' : ''} uploaded and attached to task`);
        }
      } catch {
        toast.error('Failed to upload files');
      }
    },
    [matterId, task, handleUploadToCollectionAndReturn, updateTask, taskId, rootCollectionId],
  );

  const [showOperatorMessageModal, setShowOperatorMessageModal] =
    React.useState(false);
  const [showInitialReviewModal, setShowInitialReviewModal] =
    React.useState(false);
  const [isReviewGroupCoveragesOpen, setIsReviewGroupCoveragesOpen] =
    React.useState(false);
  const [isVerify1POpen, setIsVerify1POpen] = React.useState(false);
  const [actionModalOpen, setActionModalOpen] = React.useState(false);
  const [isEditingDescription, setIsEditingDescription] = React.useState(false);
  const [dateBumpInfo, setDateBumpInfo] = React.useState<{
    newDueDate: string;
    assigneeName?: string;
    reason: 'overdue' | 'late_today';
    /** Monotonic key to force remount on each new bump */
    key: number;
  } | null>(null);

  // Push changes hook for syncing to external tasks
  const { pushChanges, pushChangesMultiple } = usePushChanges();

  // External task linking state
  const [isLinkingExternalTasks, setIsLinkingExternalTasks] =
    React.useState(false);

  // External task linking mutation
  const [linkExternalTask, { isLoading: isLinkingExternalTask }] =
    externalTaskApi.useExternalTaskLinkByInternalTaskMutation();

  // Floating UI setup for external task linking panel
  const { refs, floatingStyles, context } = useFloating({
    open: isLinkingExternalTasks,
    onOpenChange: setIsLinkingExternalTasks,
    placement: 'left',
    middleware: [offset(16), shift({ padding: 10 })],
    whileElementsMounted: autoUpdate,
  });

  const floatingClick = useClick(context);
  const floatingDismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    floatingClick,
    floatingDismiss,
  ]);

  // Handle request modal from treatment record requests
  const requestIdFromUrl = searchParams.get('request_id');
  const providerIdFromUrl = searchParams.get('provider_id');
  const [isRequestModalOpen, setIsRequestModalOpen] = React.useState(false);

  const treatmentRequestId = task?.treatment_record_request?.id;
  const requestProviderId = task?.treatment_record_request?.provider;
  const { data: requestProvider } = useGetMedicalProviderQuery(
    {
      matterId: matterId!,
      providerId: requestProviderId!,
    },
    {
      skip: !matterId || !requestProviderId,
    },
  );
  const { data: treatmentRequest } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsRetrieveQuery(
      {
        matterId: matterId!,
        providerId: requestProviderId!,
        requestId: treatmentRequestId!,
      },
      {
        skip: !matterId || !requestProviderId || !treatmentRequestId,
      },
    );

  const handleCloseRequestModal = () => {
    setIsRequestModalOpen(false);

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('request_id');
    newSearchParams.delete('provider_id');
    navigate(
      {
        search: newSearchParams.toString(),
      },
      { replace: true },
    );
  };

  const handleOpenRequestModal = React.useCallback(() => {
    const requestId = task?.treatment_record_request?.id;
    const providerId = task?.treatment_record_request?.provider;

    if (!requestId || !providerId) {
      return;
    }

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('request_id', requestId);
    newSearchParams.set('provider_id', providerId);
    navigate(
      {
        search: newSearchParams.toString(),
      },
      { replace: true },
    );
    setIsRequestModalOpen(true);
  }, [
    navigate,
    searchParams,
    task?.treatment_record_request?.id,
    task?.treatment_record_request?.provider,
  ]);

  // Police-related handlers - defined unconditionally to avoid hook rules violations
  const handlePoliceReportSelect = React.useCallback(
    (reportId: string) => {
      if (matterId) {
        window.open(
          `/matters/${matterId}/police/${reportId}`,
          '_blank',
          'noopener,noreferrer',
        );
      }
    },
    [matterId],
  );

  const handleCreatePoliceReport = React.useCallback(() => {
    if (matterId) {
      window.open(
        `/matters/${matterId}/police`,
        '_blank',
        'noopener,noreferrer',
      );
    }
  }, [matterId]);

  const handleCoverageChange = React.useCallback(
    async (nextCoverageId: string | null) => {
      if (!matterId) return;
      setIsUpdatingCoverageLink(true);
      try {
        await updateTask({
          matterId,
          taskId,
          task: { coverage_id: nextCoverageId },
        }).unwrap();
        toast.success(
          nextCoverageId ? 'Task linked to coverage' : 'Coverage link removed',
        );
      } catch (error) {
        toast.error('Failed to update coverage link');
      } finally {
        setIsUpdatingCoverageLink(false);
      }
    },
    [matterId, taskId, updateTask],
  );

  const handleConfirmLinkCoverage = React.useCallback(async () => {
    if (!selectedCoverageForLink) return;
    await handleCoverageChange(selectedCoverageForLink);
    setIsLinkCoverageDialogOpen(false);
  }, [handleCoverageChange, selectedCoverageForLink]);

  React.useEffect(() => {
    if (!task?.id || explicitCoverageId || !inferredCoverage) {
      return;
    }
    if (autoLinkedTaskRef.current === task.id) {
      return;
    }

    autoLinkedTaskRef.current = task.id;
    handleCoverageChange(inferredCoverage.id).catch((error) => {
      console.error('Failed to auto-link inferred coverage to task', error);
      autoLinkedTaskRef.current = null;
    });
  }, [explicitCoverageId, handleCoverageChange, inferredCoverage, task?.id]);

  // Handle starting initial review with analytics
  const handleStartInitialReview = React.useCallback(() => {
    capture(INITIAL_REVIEW_EVENTS.TASK_PANEL_CTA_CLICKED, {
      [INITIAL_REVIEW_PROPERTIES.MATTER_ID]: matterId,
      [INITIAL_REVIEW_PROPERTIES.TASK_ID]: taskId,
    });
    setShowInitialReviewModal(true);
    setActionModalOpen(false);
  }, [capture, matterId, taskId]);

  const handleRequestLinkCoverage = React.useCallback(() => {
    if (isCoveragesLoading) return;
    if (coverageOptions.length === 0) {
      toast.info('No coverages found on this matter yet.');
      return;
    }
    if (coverageOptions.length === 1) {
      handleCoverageChange(coverageOptions[0].id).catch((error) => {
        console.error('Failed to auto-link coverage to task', error);
      });
      return;
    }
    setSelectedCoverageForLink((prev) => prev ?? coverageOptions[0].id);
    setIsLinkCoverageDialogOpen(true);
  }, [coverageOptions, handleCoverageChange, isCoveragesLoading]);


  // Fetch deliverables and data requirements for this task
  const { data: deliverablesData } =
    deliverablesApi.useTaskDeliverablesListQuery(
      { matterId: matterId!, taskId },
      { skip: !matterId },
    );
  const deliverables = deliverablesData || [];

  // Track if we should poll for external task updates
  const [shouldPollExternalTasks, setShouldPollExternalTasks] =
    React.useState(false);

  // Fetch external tasks linked to this internal task
  // Poll every 3 seconds if there are pending/in-progress actions
  const { data: linkedExternalTasks = [] } =
    externalTaskApi.useExternalTaskByInternalTaskQuery(
      { internalTaskId: taskId },
      {
        skip: !taskId,
        pollingInterval: shouldPollExternalTasks ? 3000 : undefined,
        skipPollingIfUnfocused: true,
      },
    );

  // Update polling state when linked tasks change
  React.useEffect(() => {
    const hasPendingActions = linkedExternalTasks.some((t) =>
      t.pending_actions?.some(
        (a) => a.status === 'PENDING' || a.status === 'IN_PROGRESS',
      ),
    );
    setShouldPollExternalTasks(hasPendingActions);
  }, [linkedExternalTasks]);

  // Unlink external task mutation
  const [unlinkExternalTask] =
    externalTaskApi.useExternalTaskLinkDestroyMutation();
  const [unlinkingTaskId, setUnlinkingTaskId] = React.useState<string | null>(
    null,
  );

  const handleUnlinkExternalTask = React.useCallback(
    async (externalTask: ExternalTaskWithLinkSchema) => {
      setUnlinkingTaskId(externalTask.id);
      try {
        await unlinkExternalTask({
          externalTaskPk: externalTask.id,
          id: externalTask.link_id,
          pk: externalTask.link_id,
        }).unwrap();
        toast.success('External task unlinked');
      } catch {
        toast.error('Failed to unlink external task');
      } finally {
        setUnlinkingTaskId(null);
      }
    },
    [unlinkExternalTask],
  );

  /**
   * Check whether a task is linked to a Treatment Records Request (TRR).
   * Uses the same heuristic as the backend: `treatment_record_request_id` is set
   * AND the task is either the TRR template type or a Medical Records canonical type.
   */
  const isTrrTask = React.useMemo(() => {
    if (!task?.treatment_record_request?.id) return false;
    const isTrrTemplate =
      task.template_type === TASK_TEMPLATE_TYPES.TREATMENT_RECORD_REQUEST;
    const isMedicalRecordsType =
      typeof task.type === 'object' &&
      task.type?.canonical_type === CanonicalTaskType.MEDICAL_RECORDS;
    return isTrrTemplate || isMedicalRecordsType;
  }, [task?.treatment_record_request?.id, task?.template_type, task?.type?.canonical_type]);

  /**
   * Perform the actual status-change mutation + push-changes side effects.
   * Extracted so it can be called both from the normal flow and after the
   * TRR attachment modal flow.
   */
  const executeStatusChange = React.useCallback(
    async (
      status: { id: string; canonical_status: string },
      changeReason?: string,
      attachmentIds?: string[],
      skipAttachmentValidation?: boolean,
    ) => {
      // Track effort score before the mutation
      const previousStatus = task?.status?.canonical_status;
      if (previousStatus) {
        trackStatusUpdate(previousStatus, status.canonical_status);
      }

      const result = await updateTask({
        matterId: matterId!,
        taskId,
        task: {
          status_id: status.id,
          completed_at:
            status.canonical_status === CanonicalTaskStatus.COMPLETED
              ? new Date().toISOString()
              : null,
          change_reason: changeReason,
          ...(attachmentIds ? { attachments: attachmentIds } : {}),
          ...(skipAttachmentValidation ? { skip_attachment_validation: true } : {}),
        },
      });

      // Handle API errors — show specific toast for known codes, generic for others
      if ('error' in result) {
        const errorData = (result.error as { data?: { code?: string; error?: string } })?.data;
        if (errorData?.code === 'PREMISES_LOR_CONTACT_REQUIRED') {
          toast.error(
            errorData.error ||
              'Cannot complete Send LOR: a property contact with a valid mailing address is required before sending.',
          );
        } else {
          toast.error(errorData?.error || 'Failed to update task status.');
        }
        return;
      }

      // Show Task CSAT survey when task is marked as completed (5% sample rate, once per day max)
      if (
        status.canonical_status === CanonicalTaskStatus.COMPLETED &&
        Math.random() < 0.05 &&
        canShowCSATSurvey(POSTHOG_SURVEYS.TASK_CSAT.id)
      ) {
        markCSATSurveyShown(POSTHOG_SURVEYS.TASK_CSAT.id);
        setShowCSATSurvey(true);
      }

      // If completing, no external task linked, and matter has a CMS → prompt push note
      if (
        status.canonical_status === CanonicalTaskStatus.COMPLETED &&
        linkedExternalTasks.length === 0 &&
        cmsResolution
      ) {
        promptPushNote({
          matterId: matterId!,
          rawContent: buildTaskCompleteContext(task!, taskUpdates ?? [], changeReason),
          triggerType: 'task_complete',
          cmsResolution,
          taskTitle: task?.name,
          matterName: task?.matter_name,
          sourceType: 'TASK_COMPLETE',
          sourceId: taskId,
        });
      }

      // Push status change to external tasks
      const changesToPush: {
        changeType: 'status' | 'due_date' | 'comment';
        changeData: {
          status?: string;
          due_date?: string;
          comment?: string;
        };
      }[] = [
        {
          changeType: 'status',
          changeData: { status: status.canonical_status },
        },
      ];

      if (changeReason) {
        changesToPush.push({
          changeType: 'comment',
          changeData: { comment: changeReason },
        });
      }

      await pushChanges({
        internalTaskId: taskId,
        changes: changesToPush,
      });
    },
    [matterId, taskId, updateTask, task, trackStatusUpdate, pushChanges, linkedExternalTasks, cmsResolution, taskUpdates, promptPushNote],
  );

  /**
   * Callback invoked by the DocumentSelectionModal when the user selects
   * (or uploads) files for a TRR task completion.
   */
  const handleTrrAttachmentSelect = React.useCallback(
    async (blobIds: string[]) => {
      const pending = pendingTrrStatusRef.current;
      if (!pending) {
        return;
      }
      if (blobIds.length === 0) {
        toast.error(
          'Please select at least one file to complete this task.',
        );
        return;
      }

      try {
        await executeStatusChange(
          pending.status,
          pending.changeReason,
          blobIds,
        );
        toast.success('Medical records uploaded and task completed');
        pendingTrrStatusRef.current = null;
      } catch (e) {
        toast.error('Failed to complete task. Please try again.');
        throw e;
      }
    },
    [executeStatusChange],
  );

  /**
   * Bypass handler for "No Docs Needed" — completes task without attachments.
   * Logs the reason as a task update so there's an audit trail.
   */
  const handleTrrBypass = React.useCallback(async (reason: string) => {
    const pending = pendingTrrStatusRef.current;
    if (!pending) {
      return;
    }

    try {
      await executeStatusChange(
        pending.status,
        pending.changeReason,
        undefined,
        true,
      );

      // Log the bypass reason as a task update
      if (matterId && reason) {
        await createTaskUpdate({
          matterId,
          taskId,
          update: {
            update: `Completed without documents: ${reason}`,
            visibility: TaskUpdateVisibility.PRIVATE,
          },
        });
      }

      toast.success('Task completed — no docs needed');
      pendingTrrStatusRef.current = null;
      setTrrAttachmentModalOpen(false);
    } catch {
      toast.error('Failed to complete task. Please try again.');
    }
  }, [executeStatusChange, matterId, taskId, createTaskUpdate]);

  // Handler for status changes - shows modal for certain statuses, otherwise updates directly
  const handleStatusChange = React.useCallback(
    async (status: { id: string; canonical_status: string }) => {
      let changeReason: string | undefined;

      const isTreatmentCheckInProtected =
        isTreatmentCheckInTask &&
        TREATMENT_CHECK_IN_PROTECTED_STATUSES.includes(
          status.canonical_status as CanonicalTaskStatus,
        );

      const needsReason =
        requiresStatusChangeReason(status.canonical_status) ||
        isTreatmentCheckInProtected;

      if (needsReason) {
        const result = await requestChangeReason({
          fieldType: 'status',
          oldValue: task?.status?.canonical_status ?? null,
          newValue: status.canonical_status,
          taskName: task?.name ?? '',
          recentUpdate: recentUserUpdate?.text,
          recentUpdateCreatedAt: recentUserUpdate?.createdAt,
          warningMessage: isTreatmentCheckInProtected
            ? TREATMENT_CHECK_IN_STATUS_WARNING
            : undefined,
          requiredCheckboxes: isTreatmentCheckInProtected
            ? TREATMENT_CHECK_IN_CHECKBOXES
            : undefined,
        });

        if (!result) {
          return;
        }

        if (isTreatmentCheckInProtected) {
          changeReason = buildTreatmentCheckInComment(result.reasonDetail);
        } else {
          changeReason = result.reasonDetail || result.reason;
        }
      }

      // If this is a TRR task being completed/closed, check whether the TRR
      // has already been advanced to records_ingested (i.e. records were already
      // uploaded via the TRR status tracker). If so, skip the document selection
      // modal and complete the task directly with skip_attachment_validation.
      const isCompletionStatus =
        status.canonical_status === CanonicalTaskStatus.COMPLETED ||
        status.canonical_status === CanonicalTaskStatus.CLOSED;

      if (isTrrTask && isCompletionStatus) {
        // "Closed" never requires a file upload — it means the task is being
        // dismissed / closed-out without records delivery.
        if (status.canonical_status === CanonicalTaskStatus.CLOSED) {
          await executeStatusChange(status, changeReason, undefined, true);
          return;
        }
        const trrStatus = task?.treatment_record_request?.status;
        if (trrStatus === 'records_ingested') {
          // Records were already uploaded via TRR tracker — complete without re-upload
          await executeStatusChange(status, changeReason, undefined, true);
          return;
        }
        pendingTrrStatusRef.current = { status, changeReason };
        setTrrAttachmentModalOpen(true);
        return;
      }

      // For any task with pending deliverables that require evidence, require upload before completion
      if (
        isCompletionStatus &&
        status.canonical_status === CanonicalTaskStatus.COMPLETED &&
        deliverables.length > 0
      ) {
        const pendingRequiringEvidence = deliverables.filter(
          (d) => d.status === 'Pending' && d.requires_evidence,
        );
        if (pendingRequiringEvidence.length > 0) {
          // Reuse the same document selection modal as TRR tasks
          pendingTrrStatusRef.current = { status, changeReason };
          setTrrAttachmentModalOpen(true);
          return;
        }
      }

      await executeStatusChange(status, changeReason);
    },
    [
      matterId,
      taskId,
      updateTask,
      task,
      trackStatusUpdate,
      requestChangeReason,
      recentUserUpdate,
      pushChanges,
      isTreatmentCheckInTask,
      isTrrTask,
      executeStatusChange,
      deliverables,
    ],
  );

  // Expose handleStatusChange to parent components via ref
  // This allows the panel header to delegate status changes to TaskDetail's full validation logic
  React.useEffect(() => {
    if (onStatusChangeRef) {
      onStatusChangeRef.current = handleStatusChange;
    }
    return () => {
      if (onStatusChangeRef) {
        onStatusChangeRef.current = null;
      }
    };
  }, [onStatusChangeRef, handleStatusChange]);

  // Handler for due date changes - shows modal if task has existing due_date
  const handleDueDateChange = React.useCallback(
    async (date: Date | null) => {
      if (!date) return;

      const dueDateStr = createConsistentDate(date);
      let changeReason: string | undefined;
      let selectedTaskIds: string[] | undefined;
      let selectedTasks: { id: string; name: string }[] | undefined;

      // Check if due date change requires a reason (centralized in task-field-change-reasons.ts)
      if (requiresDueDateChangeReason(task?.due_date, dueDateStr)) {
        const result = await requestChangeReason({
          fieldType: 'due_date',
          oldValue: task?.due_date ?? null,
          newValue: dueDateStr,
          taskName: task?.name ?? '',
          // Pass other Initial Review tasks for bulk update option
          otherTasks: isInitialReviewTask ? bulkUpdateInitialReviewTasks : undefined,
          recentUpdate: recentUserUpdate?.text,
          recentUpdateCreatedAt: recentUserUpdate?.createdAt,
        });

        if (!result) {
          // User cancelled
          return;
        }

        // Use the detail as the reason (since 'other' is just a placeholder)
        changeReason = result.reasonDetail || result.reason;
        selectedTaskIds = result.selectedTaskIds;
        selectedTasks = result.selectedTasks;
      }

      // Update the current task
      await updateTask({
        matterId: matterId!,
        taskId,
        task: {
          due_date: dueDateStr,
          change_reason: changeReason,
        },
      });

      // Bulk update selected intake tasks using the bulk API
      if (selectedTaskIds && selectedTaskIds.length > 0) {
        try {
          const response = await bulkUpdateTasks({
            task_ids: selectedTaskIds,
            due_date: dueDateStr,
            change_reason: changeReason,
          }).unwrap();

          const totalUpdated = response.updated_count + 1; // +1 for the primary task
          if (response.skipped.length === 0) {
            toast.success(
              `Updated ${totalUpdated} task${totalUpdated !== 1 ? 's' : ''}`,
            );
          } else {
            toast.warning(
              `Updated ${totalUpdated} task${totalUpdated !== 1 ? 's' : ''}, skipped ${response.skipped.length}`,
            );
          }
        } catch {
          toast.error('Failed to update some tasks');
        }
      }

      // Push due date change to external tasks (context handles fetching and filtering)
      // Include the reason as a comment if one was provided
      const changesToPush: {
        changeType: 'status' | 'due_date' | 'comment';
        changeData: {
          status?: string;
          due_date?: string;
          comment?: string;
        };
      }[] = [{ changeType: 'due_date', changeData: { due_date: dueDateStr } }];

      if (changeReason) {
        changesToPush.push({
          changeType: 'comment',
          changeData: { comment: changeReason },
        });
      }

      // Use pushChangesMultiple when companion tasks were selected, otherwise single push
      if (selectedTasks && selectedTasks.length > 0) {
        await pushChangesMultiple({
          internalTasks: [
            { id: taskId, name: task?.name ?? '' },
            ...selectedTasks,
          ],
          changes: changesToPush,
        });
      } else {
        await pushChanges({
          internalTaskId: taskId,
          changes: changesToPush,
        });
      }
    },
    [
      matterId,
      taskId,
      updateTask,
      bulkUpdateTasks,
      task?.due_date,
      task?.name,
      requestChangeReason,
      recentUserUpdate,
      isInitialReviewTask,
      bulkUpdateInitialReviewTasks,
      pushChanges,
      pushChangesMultiple,
    ],
  );

  // Handler for creating comments - creates update and optionally shows push dialog
  const handleCreateComment = React.useCallback(
    async (update: string, visibility: TaskUpdateVisibility, alertMentioned = false) => {
      const taskUpdate = await createTaskUpdate({
        matterId: matterId!,
        taskId,
        update: {
          update,
          visibility,
          alert_mentioned: alertMentioned,
        },
      }).unwrap();

      // Push comment to external tasks (context handles fetching and filtering)
      await pushChanges({
        internalTaskId: taskId,
        changes: [
          {
            changeType: 'comment',
            changeData: {
              comment: update,
              source_task_update_id: taskUpdate.id,
            },
          },
        ],
      });
    },
    [matterId, taskId, createTaskUpdate, pushChanges],
  );

  // Handler for linking external tasks to this internal task
  const handleLinkExternalTasks = React.useCallback(
    async (externalTaskIds: string[]) => {
      try {
        await Promise.all(
          externalTaskIds.map((externalTaskId) =>
            linkExternalTask({
              internalTaskId: taskId,
              body: { external_task_id: externalTaskId },
            }).unwrap(),
          ),
        );
        toast.success(
          `Linked ${externalTaskIds.length} external task${externalTaskIds.length !== 1 ? 's' : ''}`,
        );
      } catch (err) {
        toast.error('Failed to link external tasks');
      }
    },
    [taskId, linkExternalTask],
  );

  // SEND_LOR: Prepare auto-drafted LoR view state via hook
  const isSendLOR = task?.type?.canonical_type === CanonicalTaskType.SEND_LOR;

  // Police-related task detection
  const isPoliceRelatedTask = React.useMemo(() => {
    if (!task) return false;
    const canonicalType = task.type?.canonical_type as
      | CanonicalTaskType
      | undefined;
    const groupName = task.group?.name;

    return (
      canonicalType === CanonicalTaskType.POLICE_REPORT ||
      canonicalType === CanonicalTaskType.LOCATE_REPORT_SOURCE ||
      groupName === 'Police Report & Investigation'
    );
  }, [task]);

  // Premises liability task detection
  const isPremisesLiabilityTask = React.useMemo(() => {
    if (!task) return false;
    return (
      taskCanonicalType === CanonicalTaskType.REVIEW_INCIDENT_PROPERTIES ||
      taskCanonicalType === CanonicalTaskType.RESEARCH_PROPERTY_OWNERS ||
      taskCanonicalType === CanonicalTaskType.OPEN_PREMISES_CLAIM ||
      taskCanonicalType === CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS
    );
  }, [task, taskCanonicalType]);

  const latestPremisesLorPackage = React.useMemo(() => {
    if (!premisesLorPackages || !linkedCoverageIdForNavigation) {
      return undefined;
    }

    return premisesLorPackages
      .filter((pkg) => pkg.coverage_id === linkedCoverageIdForNavigation)
      .sort((a, b) => {
        const aTime = new Date(
          a.transmitted_at || a.modified_at || a.created_at || 0,
        ).getTime();
        const bTime = new Date(
          b.transmitted_at || b.modified_at || b.created_at || 0,
        ).getTime();
        return bTime - aTime;
      })[0];
  }, [linkedCoverageIdForNavigation, premisesLorPackages]);

  const displayedTaskDescription = React.useMemo(() => {
    if (taskCanonicalType === CanonicalTaskType.OPEN_PREMISES_CLAIM) {
      return "Call the property contact to verify LOR receipt, get claim number, and open the claim.";
    }
    if (taskCanonicalType === CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS) {
      return "Research and save updated property contact information so a fresh LOR task can be generated.";
    }
    return task?.description || "No description yet";
  }, [task?.description, taskCanonicalType]);

  // Property Lookup drawer state
  const [isPropertyLookupOpen, setIsPropertyLookupOpen] = React.useState(false);

  const isMedicalProviderRelatedTask = React.useMemo(
    () => Boolean(task?.treatment_record_request?.id),
    [task?.treatment_record_request?.id],
  );

  // Only user-created tasks can be deleted, and only within 5 minutes of creation
  const canDeleteTask = React.useMemo(() => {
    if (!task?.created_at) return false;
    if (task.source !== 'finch_user') return false;
    const DELETION_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
    const createdAt = new Date(task.created_at);
    const now = new Date();
    return now.getTime() - createdAt.getTime() < DELETION_WINDOW_MS;
  }, [task?.created_at, task?.source]);

  // Save description explicitly (no auto-save)
  const saveNotesFunction = React.useCallback(
    async (notes: string) => {
      if (!task) return;

      await updateTask({
        matterId: matterId!,
        taskId,
        task: { description: notes },
      }).unwrap();
    },
    [updateTask, matterId, taskId, task],
  );

  // Set internal notes when task data is loaded (but only if not actively editing)
  React.useEffect(() => {
    if (task && !isEditingDescription) {
      setInternalNotes(task.description || '');
    }
  }, [task, isEditingDescription]);

  // Handle notes change
  const handleNotesChange = React.useCallback((notes: string) => {
    setInternalNotes(notes);
  }, []);

  // Action button click handler (must be before early returns to satisfy React hooks rules)
  const handleActionButtonClick = React.useCallback(() => {
    if (!task) return;
    const config = getTaskActionConfig(task);
    if (!config) return;

    const canonicalType =
      typeof task.type === 'object'
        ? (task.type?.canonical_type as CanonicalTaskType | undefined)
        : undefined;

    // Initial review: open the review modal directly (skip intermediary action modal)
    if (canonicalType === CanonicalTaskType.CONDUCT_INITIAL_REVIEW) {
      handleStartInitialReview();
      return;
    }

    if (config.mode === 'request_modal') {
      handleOpenRequestModal();
      return;
    }

    if (config.mode === 'link') {
      // Deep link: navigate directly for claims review tasks
      if (!matterId) return;

      // Matter-level premises review: anchor on a PROPERTY coverage for the stepper URL,
      // not task.coverage_id (often absent or a non-property coverage).
      if (canonicalType === CanonicalTaskType.REVIEW_INCIDENT_PROPERTIES) {
        const anchorId = premisesReviewStepperCoverageId;
        if (anchorId) {
          navigate(`/matters/${matterId}/premises/${anchorId}/confirm-location`);
        } else {
          navigate(`/matters/${matterId}/coverage/create`);
        }
        return;
      }

      // No coverage linked yet — prompt user to link one
      if (!linkedCoverageId) {
        handleRequestLinkCoverage();
        return;
      }

      if (canonicalType === CanonicalTaskType.RESEARCH_PROPERTY_OWNERS) {
        navigate(`/matters/${matterId}/premises/${linkedCoverageId}/research-property`);
      } else if (
        canonicalType === CanonicalTaskType.OPEN_PREMISES_CLAIM ||
        canonicalType === CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS
      ) {
        const followUpHref = buildPremisesFollowUpUrlForType(
          matterId,
          linkedCoverageId,
          canonicalType,
        );
        if (followUpHref) {
          navigate(followUpHref);
        }
      } else if (canonicalType === CanonicalTaskType.LOR_ACKNOWLEDGEMENT) {
        navigate(buildCoverageOverviewUrl(matterId, linkedCoverageId));
      } else {
        const statusName =
          task.status?.name?.toLowerCase() ??
          task.status?.canonical_status?.toLowerCase() ??
          '';
        const isPremisesCoverage =
          linkedCoverageForNavigation?.category === CoverageCategory.PROPERTY;
        const stepParam =
          canonicalType === CanonicalTaskType.REVIEW_FOUND_COVERAGE
            ? 'review'
            : canonicalType === CanonicalTaskType.OPEN_CLAIM
              ? 'call'
              : canonicalType === CanonicalTaskType.SEND_LOR &&
                  isPremisesCoverage
                ? resolvePremisesLorStep({
                    lorDone: sendLorPremisesDone,
                    reviewSendDone: reviewSendPremisesDone,
                  })
              : canonicalType === CanonicalTaskType.SEND_LOR &&
                  statusName.includes('under review')
                ? 'review_send'
              : 'lor';
        navigate(buildClaimsFilingUrl(matterId, linkedCoverageId, stepParam));
      }
    } else {
      setActionModalOpen(true);
    }
  }, [
    task,
    linkedCoverageId,
    matterId,
    navigate,
    handleOpenRequestModal,
    handleStartInitialReview,
    handleRequestLinkCoverage,
    linkedCoverageIdForNavigation,
    linkedCoverageForNavigation?.category,
    premisesReviewStepperCoverageId,
    sendLorPremisesDone,
    reviewSendPremisesDone,
  ]);

  if (isTaskLoading && !task) {
    return (
      <div className="relative flex flex-col items-center justify-center h-full w-full">
        {/* Infinite progress bar at the top */}
        <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden bg-transparent">
          <div className="h-[2px] w-1/3 bg-blue-500 dark:bg-blue-400 rounded-full animate-[datatable-shimmer_1.2s_ease-in-out_infinite]" />
        </div>
        <p className="text-sm font-medium text-zinc-500 mt-4">Loading task...</p>
      </div>
    );
  }

  if (taskError || !task) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-red-600">Error</h3>
          <p className="text-sm text-slate-500">
            Failed to load task details. Please try again.
          </p>
        </div>
      </div>
    );
  }

  const handleDeleteTask = async () => {
    try {
      await deleteTask({ matterId: matterId!, taskId });
      setIsDeleteAlertOpen(false);
      toast.success('Task deleted successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const performTaskAction = (action: PhoneCallAction): void => {
    try {
      if (!task) return;

      // Create a task update for the phone call
      createTaskUpdate({
        matterId: matterId!,
        taskId,
        update: {
          update: `Phone call to ${action.phone_number || 'unknown number'}.`,
          visibility: TaskUpdateVisibility.PRIVATE,
        },
      });

      // Refetch updates to show the new one
      refetchTaskUpdates();
    } catch (error) {
      toast.error('Failed to perform task action');
    }
  };

  const defaultMetadataSlot = (
    <TaskMetadata
      task={taskWithResolvedCoverage || task}
      staffUsers={filteredStaffUsers || []}
      isStaffUsersLoading={isStaffUsersLoading}
      coverages={coverageOptions}
      isCoveragesLoading={isCoveragesLoading}
      isCoverageUpdating={isUpdatingCoverageLink}
      onCoverageChange={handleCoverageChange}
      onAssigneeChange={(userId) => {
        const currentTask = taskWithResolvedCoverage || task;
        const patch: Record<string, unknown> = { assignee_id: userId };

        // Only compute bump if the firm has the feature enabled (default: on)
        const firmFlags = matterDetails?.firm?.feature_flags;
        const bumpEnabled =
          !firmFlags?.reassignment_date_bump_enabled ||
          firmFlags.reassignment_date_bump_enabled.toLowerCase() !== 'false';

        if (bumpEnabled) {
          const bump = computeReassignmentDateBump(currentTask?.due_date);
          if (bump.shouldBump && bump.newDueDate) {
            const bumpedDate = bump.newDueDate;
            const bumpedReason = bump.reason!;
            patch.due_date = bumpedDate;
            const assignee = filteredStaffUsers?.find((u) => u.id === userId);
            setDateBumpInfo((prev) => ({
              newDueDate: bumpedDate,
              assigneeName: assignee
                ? `${assignee.first_name} ${assignee.last_name}`.trim()
                : undefined,
              reason: bumpedReason,
              key: (prev?.key ?? 0) + 1,
            }));
          }
        }

        updateTask({
          matterId: matterId!,
          taskId,
          task: patch,
        });
      }}
      onDueDateChange={handleDueDateChange}
      onCompletedDateChange={(date) => {
        updateTask({
          matterId: matterId!,
          taskId,
          task: {
            completed_at: date ? createConsistentDate(date) : null,
          },
        });
      }}
      onOrderChange={(order) =>
        updateTask({
          matterId: matterId!,
          taskId,
          task: { order },
        })
      }
      onCMSUpdateStatusChange={async (status) => {
        try {
          await updateTaskCMSStatus({
            matterId: matterId!,
            taskId,
            data: {
              external_cms_update_status: status,
            },
          }).unwrap();
          toast.success('Task marked as updated in CMS');
        } catch (error) {
          toast.error('Failed to update CMS status');
          console.error('CMS update error:', error);
        }
      }}
      managingParalegal={matterDetails?.firm?.managing_paralegal}
      caseManager={matterDetails?.case_manager}
      pushAction={taskPushAction}
      isWatched={isWatched}
      onWatchToggle={
        // Hide bell on completed tasks unless user is already watching
        task?.status?.canonical_status === 'Completed' && !isWatched
          ? undefined
          : () => {
              if (!matterId) return;
              const newState = !isWatched;
              setOptimisticWatched(newState);
              const mutation = newState ? watchTask : unwatchTask;
              mutation({ matterId, taskId }).unwrap().catch(() => {
                setOptimisticWatched(!newState);
              });
            }
      }
    />
  );

  const medicalRequestMetadataSlot =
    isMedicalProviderRelatedTask &&
    matterId &&
    treatmentRequest &&
    requestProvider ? (
      <div className="space-y-1.5">
        {defaultMetadataSlot}
        <div className="flex items-center gap-3 whitespace-nowrap">
          <div className="inline-flex items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">
              Task
            </span>
            <TaskStatusBadge
              status={task.status}
              onStatusChange={handleStatusChange}
              task={task}
              className="min-w-[88px] rounded-md border border-slate-200 px-2 py-0.5 text-[11px] shadow-none"
            />
          </div>

          <div className="inline-flex items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">
              Request
            </span>
            <MatterProvider matterId={matterId}>
              <InteractiveRequestStatusBadge
                request={treatmentRequest}
                providerId={requestProvider.id}
                className="rounded-md border border-slate-200 px-2 py-0.5 text-[11px] shadow-none"
              />
            </MatterProvider>
          </div>

          {completedTaskStatus &&
            task.status.canonical_status !== CanonicalTaskStatus.COMPLETED && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 rounded-md border border-slate-200 px-2 text-[11px] font-medium shadow-none"
                onClick={() => handleStatusChange(completedTaskStatus)}
              >
                Complete
              </Button>
            )}

          {matterId && task.treatment_record_request && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium text-indigo-600 hover:bg-indigo-50 transition-colors disabled:opacity-50"
              disabled={isEvalStarting}
              onClick={async () => {
                try {
                  await startEval({
                    matterId,
                    requestId: task.treatment_record_request!.id,
                  }).unwrap();
                  toast.success('Agent run started');
                } catch {
                  toast.error('Failed to start agent run');
                }
              }}
            >
              <Zap className="h-3 w-3" />
              {isEvalStarting ? 'Starting...' : 'Run Agent'}
            </button>
          )}
        </div>
      </div>
    ) : null;

  const metadataSlot = medicalRequestMetadataSlot ?? defaultMetadataSlot;
  const taskActionConfig = taskWithResolvedCoverage
    ? getTaskActionConfig(taskWithResolvedCoverage)
    : null;
  const shouldShowUpworkEscalationStatusLine =
    Boolean(matterId) &&
    task?.type?.canonical_type === CanonicalTaskType.MEDICAL_RECORDS &&
    taskActionConfig?.label === 'Follow Up with Provider';

  // Whether this task has an associated treatment record request that the
  // agent can prepare a submission attempt for.
  const hasAgentEligibleRequest = (() => {
    return Boolean(
      isMedicalProviderRelatedTask &&
        task.treatment_record_request?.id &&
        isSubmitRequestTask(task),
    );
  })();

  const requestEntryCard = isMedicalProviderRelatedTask ? (
    requestProvider &&
    matterId &&
    task.treatment_record_request && (
      <TreatmentRequestEntrySheet
        matterId={matterId}
        provider={requestProvider}
        requestId={task.treatment_record_request.id}
        compactForMatter={Boolean(urlMatterId)}
      />
    )
  ) : null;

  const actionButton = isMedicalProviderRelatedTask ? (
    <div className="flex w-full flex-col items-start space-y-1.5">
      {shouldShowUpworkEscalationStatusLine && matterId && (
        <UpworkEscalationStatusLine taskId={task.id} matterId={matterId} />
      )}
      {hasAgentEligibleRequest && matterId && task.treatment_record_request ? (
        <RecordsRequestAgentButton
          matterId={matterId}
          treatmentRequestId={task.treatment_record_request.id}
          onOpenRequestModal={handleOpenRequestModal}
        />
      ) : (
        <TaskActionButton
          task={taskWithResolvedCoverage || task}
          onClick={handleActionButtonClick}
        />
      )}
      {requestEntryCard}
    </div>
  ) : (
    <TaskActionButton
      task={taskWithResolvedCoverage || task}
      onClick={handleActionButtonClick}
    />
  );

  const taskDrawerWidthClass =
    isPoliceRelatedTask && isModal
      ? 'max-w-[75vw] w-[75vw]'
      : isMedicalProviderRelatedTask
        ? '!min-w-0 !w-[min(620px,calc(100vw-2rem))] !max-w-[620px]'
        : isPremisesLiabilityTask || isSendLOR
          ? 'min-w-0 w-[min(860px,calc(100vw-2rem))] max-w-[860px]'
          : 'min-w-0 w-[clamp(720px,46vw,920px)] max-w-[calc(100vw-2rem)]';

  // Extract task detail sections into a reusable function
  const instructionSlot = matterId && task?.id ? (
    <TaskInstructionCard matterId={matterId} taskId={task.id} />
  ) : null;

  const noticeSlot = dateBumpInfo ? (
    <ReassignmentDateBumpNotice
      key={dateBumpInfo.key}
      newDueDate={dateBumpInfo.newDueDate}
      assigneeName={dateBumpInfo.assigneeName}
      reason={dateBumpInfo.reason}
    />
  ) : null;

  const renderTaskDetailSections = () => (
    <>
      {/* Description Section - Show task description if it exists */}
      <TaskSection
        title={
          <span className="inline-flex items-center gap-1.5">
            Description
            {!isEditingDescription && (
              <button
                type="button"
                onClick={() => {
                  preEditDescriptionRef.current = task?.description || '';
                  setIsEditingDescription(true);
                }}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <Pencil className="h-2.5 w-2.5" />
              </button>
            )}
          </span>
        }
        className="px-4"
        compact
      >
        {isEditingDescription ? (
          <div className="space-y-1.5">
            {internalNotes === null ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-pulse text-slate-500 text-xs">
                  Loading...
                </div>
              </div>
            ) : (
              <TaskInternalNotes
                notes={internalNotes}
                onNotesChange={handleNotesChange}
              />
            )}
            <div className="flex justify-end gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setInternalNotes(preEditDescriptionRef.current);
                  setIsEditingDescription(false);
                }}
                className="h-6 text-[10px] px-3"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={async () => {
                  if (internalNotes !== null) {
                    try {
                      await saveNotesFunction(internalNotes);
                      setIsEditingDescription(false);
                    } catch {
                      toast.error('Failed to save description');
                    }
                  } else {
                    setIsEditingDescription(false);
                  }
                }}
                className="h-6 text-[10px] px-3"
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            <TaskDescription description={displayedTaskDescription} />
            {/* Guidelines - inline below description */}
            {task.type?.canonical_type &&
              matterId &&
              taskCanonicalType !== CanonicalTaskType.OPEN_PREMISES_CLAIM && (
              <TaskGuidelines
                matterId={matterId}
                taskType={task.type.canonical_type}
              />
              )}
          </>
        )}
      </TaskSection>
      {/* Updates — always visible, scrollable */}
      <TaskSection noPadding className="px-2">
        <Timeline
          matterId={matterId!}
          taskId={taskId}
          linkedExternalTasks={linkedExternalTasks}
          onCreateUpdate={handleCreateComment}
          onUpdateUpdate={async (updateId, update, visibility) => {
            await updateTaskUpdate({
              matterId: matterId!,
              taskId,
              updateId,
              update: {
                update,
                visibility,
              },
            });
          }}
          onDeleteUpdate={async (updateId) => {
            await deleteTaskUpdate({
              matterId: matterId!,
              taskId,
              updateId,
            });
          }}
        />
      </TaskSection>

      {/* AI Hold & Transfer - for tasks with coverage */}
      {matterId && linkedCoverageId && (
        <AIHoldAndTransferCard task={taskWithResolvedCoverage || task} matterId={matterId} />
      )}

      {matterId && linkedCoverage && linkedCoverageId && (
        <details open className="group border-b border-slate-200">
          <summary className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-slate-50 transition-colors">
            <span className="text-xs font-semibold text-slate-700">Coverage</span>
            <div className="flex items-center gap-2">
              {!explicitCoverageId && inferredCoverage && (
                <span className="text-[10px] font-medium text-amber-700">auto-linked</span>
              )}
              <ChevronDown className="h-3.5 w-3.5 text-slate-400 transition-transform group-open:rotate-180" />
            </div>
          </summary>
          <div className="px-4 pb-4">
            {!explicitCoverageId && inferredCoverage && (
              <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                This task was missing an explicit coverage link. Using the only approved matching
                coverage on this matter.
              </div>
            )}
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <CoverageCardRedesign
                coverage={linkedCoverage}
                matterId={matterId}
                readOnly
                forceShowProgressStepper
              />
            </div>
          </div>
        </details>
      )}

      {showAgentRunDetails && linkedCoverageId && (
        <details open className="group border-b border-slate-200">
          <summary className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-slate-50 transition-colors">
            <span className="text-xs font-semibold text-slate-700">Agent Run</span>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-medium text-slate-500">{agentStatusLabel}</span>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400 transition-transform group-open:rotate-180" />
            </div>
          </summary>
          <div className="px-4 pb-4">
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <CrashingOutRunDetailsPanel
                matterId={matterId!}
                coverageId={linkedCoverageId}
                initialDisplayStatus={task.crashing_out_agent_status}
                initialMatterName={matterDetails?.name || task.matter_name || null}
                initialCoverageName={linkedCoverage?.carrier?.name ?? null}
                variant="inline"
              />
            </div>
          </div>
        </details>
      )}

      {/* Accordions for secondary sections */}
      <div>
        {/* Related Activity */}
        <details className="group border-b border-slate-200">
          <summary className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-slate-50 transition-colors">
            <span className="text-xs font-semibold text-slate-700">Related Activity</span>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400 transition-transform group-open:rotate-180" />
          </summary>
          <div className="px-2 pb-2">
            <RelatedActivityTimeline matterId={matterId!} taskId={taskId} />
          </div>
        </details>

        {/* Companion Tasks */}
        {(task.companion_count ?? 0) > 1 && (
          <details className="group border-b border-slate-200">
            <summary className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-slate-50 transition-colors">
              <span className="text-xs font-semibold text-slate-700">Companion Tasks</span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-blue-600">{task.companion_count}</span>
                <ChevronDown className="h-3.5 w-3.5 text-slate-400 transition-transform group-open:rotate-180" />
              </div>
            </summary>
            <div className="px-2 pb-2">
              <CompanionTasksTab matterId={matterId!} taskId={taskId} />
            </div>
          </details>
        )}

        {/* Attachments & Requirements */}
        <details className="group border-b border-slate-200">
          <summary className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-slate-50 transition-colors">
            <span className="text-xs font-semibold text-slate-700">Attachments</span>
            <div className="flex items-center gap-2">
              {(task.attachments?.length || 0) > 0 && (
                <span className="text-[10px] font-semibold text-blue-600">
                  {task.attachments?.length || 0}
                </span>
              )}
              <ChevronDown className="h-3.5 w-3.5 text-slate-400 transition-transform group-open:rotate-180" />
            </div>
          </summary>
          <div className="px-2 pb-2 space-y-3">
            <TaskAttachments
              task={taskWithResolvedCoverage || task}
              onDeleteAttachment={async (attachmentId) => {
                await updateTask({
                  matterId: matterId!,
                  taskId,
                  task: {
                    attachments: (task?.attachments ?? [])
                      .filter((a) => a.id !== attachmentId)
                      .map((a) => a.id),
                  },
                });
              }}
            />
            {deliverables.length > 0 && (
              <div className="pt-2 border-t border-slate-100">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Requirements</span>
                <div className="mt-1.5">
                  <DeliverablesButton
                    deliverables={deliverables as any}
                    matterId={matterId!}
                    taskId={taskId}
                    fullWidth={false}
                    showStats
                    showIcon
                    size="sm"
                    variant="outline"
                  />
                </div>
              </div>
            )}
          </div>
        </details>
      </div>

      {/* Linked External Tasks — accordion style */}
      <details className="group border-b border-slate-200">
        <summary className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-slate-50 transition-colors">
          <span className="text-xs font-semibold text-slate-700">External Tasks</span>
          <div className="flex items-center gap-2">
            {linkedExternalTasks.length > 0 && (
              <span className="text-[10px] font-semibold text-blue-600">{linkedExternalTasks.length}</span>
            )}
            <ChevronDown className="h-3.5 w-3.5 text-slate-400 transition-transform group-open:rotate-180" />
          </div>
        </summary>
        <div className="px-2 pb-2 space-y-1.5">
          {/* Link button */}
          {matterId && (
            <div ref={refs.setReference} {...getReferenceProps()}>
              <Button
                variant="outline"
                size="sm"
                className="w-full h-7 text-xs font-semibold gap-1.5"
              >
                <Link2 className="h-3 w-3" />
                {isLinkingExternalTasks ? 'Close Linking' : 'Link External Tasks'}
              </Button>
            </div>
          )}

          {/* Linked tasks */}
          {linkedExternalTasks.map(
            (externalTask: ExternalTaskWithLinkSchema) => {
              const activeAction = externalTask.pending_actions?.find(
                (a) => a.status === 'PENDING' || a.status === 'IN_PROGRESS',
              );
              const failedAction = externalTask.pending_actions?.find(
                (a) => a.status === 'FAILED',
              );

              return (
                <div
                  key={externalTask.id}
                  className="px-2 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-semibold text-slate-900 truncate">
                          {externalTask.name}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded font-medium">
                          {externalTask.status}
                        </span>
                        <span className="text-[10px] text-slate-500 font-medium">
                          {externalTask.source}
                        </span>
                      </div>
                      {activeAction && (
                        <div className="flex items-center gap-1 text-[10px] text-blue-600 mt-0.5">
                          <Loader2 className="h-2.5 w-2.5 animate-spin" />
                          <span className="font-medium">Syncing…</span>
                        </div>
                      )}
                      {!activeAction && failedAction && (
                        <div className="flex items-center gap-1 text-[10px] text-red-600 mt-0.5">
                          <AlertCircle className="h-2.5 w-2.5" />
                          <span className="font-medium">Sync failed</span>
                        </div>
                      )}
                      {(externalTask.due_date || externalTask.assignee) && (
                        <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-medium mt-0.5">
                          {externalTask.due_date && (
                            <span>
                              Due {new Date(`${externalTask.due_date.split('T')[0]}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                          {externalTask.assignee && (
                            <span>· {externalTask.assignee.name}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`/external-tasks/${externalTask.id}`, '_blank', 'noopener,noreferrer')}
                        className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="View external task"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnlinkExternalTask(externalTask)}
                        disabled={unlinkingTaskId === externalTask.id}
                        className="h-6 w-6 p-0 text-slate-500 hover:text-red-600 hover:bg-red-50"
                        title="Unlink"
                      >
                        {unlinkingTaskId === externalTask.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Unlink className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            },
          )}

          {linkedExternalTasks.length === 0 && !matterId && (
            <p className="text-[10px] text-slate-500 font-medium text-center py-1">
              No external tasks linked
            </p>
          )}
        </div>
      </details>
      {/* Police Report Context Section - Show for police report tasks */}
      {task.group?.name === 'Police Report & Investigation' &&
        task.police_report && (
          <TaskSection className="px-4" compact>
            <PoliceReportContext
              policeReport={task.police_report}
              matterId={matterId}
              onUpdate={() => {
                // Refetch task data to get updated police report
                // The task query will automatically refetch when the component re-renders
              }}
            />
          </TaskSection>
        )}
      {/* Property Lookup - Available for premises liability tasks */}
      {isPremisesLiabilityTask && (
        <>
          <TaskSection title="Tools" className="px-4" compact>
            <div className="space-y-3">
              {taskCanonicalType === CanonicalTaskType.OPEN_PREMISES_CLAIM &&
                matterId &&
                latestPremisesLorPackage && (
                  <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className="text-slate-900 font-medium">
                          Premises LOR correspondence
                        </span>
                        <p className="mt-0.5 text-xs text-slate-600">
                          {latestPremisesLorPackage.recipient_display_name
                            ? `Sent to ${latestPremisesLorPackage.recipient_display_name}`
                            : "Open the certified-mail package that was sent for this coverage."}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() =>
                          navigate(
                            `/matters/${matterId}/correspondence/${latestPremisesLorPackage.id}`,
                          )
                        }
                      >
                        <ExternalLink className="mr-2 h-3.5 w-3.5" />
                        Open Package
                      </Button>
                    </div>
                  </div>
                )}
              <Button
                variant="outline"
                size="default"
                className="w-full"
                onClick={() => setIsPropertyLookupOpen(true)}
              >
                <Building2 className="h-4 w-4 mr-2" />
                Property Lookup
              </Button>
              {researchAlternateContactsButton}
            </div>
          </TaskSection>
          <PropertyLookupDrawer
            isOpen={isPropertyLookupOpen}
            onClose={() => setIsPropertyLookupOpen(false)}
          />
        </>
      )}

      <Dialog
        open={isLinkCoverageDialogOpen}
        onOpenChange={setIsLinkCoverageDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link task to coverage</DialogTitle>
            <DialogDescription>
              Select which coverage this task should be affiliated with.
            </DialogDescription>
          </DialogHeader>
          <Select
            value={selectedCoverageForLink ?? undefined}
            onValueChange={(value) => setSelectedCoverageForLink(value)}
          >
            <SelectTrigger className="mt-4">
              <SelectValue placeholder="Select coverage" />
            </SelectTrigger>
            <SelectContent>
              {coverageOptions.map((coverage) => (
                <SelectItem key={coverage.id} value={coverage.id}>
                  {(coverage.carrier?.name || 'Coverage').trim()} ·{' '}
                  {coverage.category || 'Unknown'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsLinkCoverageDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                handleConfirmLinkCoverage().catch((error) => {
                  console.error('Failed to link coverage to task', error);
                });
              }}
              disabled={!selectedCoverageForLink || isUpdatingCoverageLink}
            >
              {isUpdatingCoverageLink ? 'Linking…' : 'Link coverage'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete Section - At the bottom */}
      <TaskSection className="px-4 pb-4" compact>
        <div className="flex items-center justify-between">
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsDeleteAlertOpen(true)}
                  disabled={isDeleting || !canDeleteTask}
                  className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 disabled:text-gray-400 disabled:hover:bg-transparent"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Task'}
                </Button>
              </span>
            </TooltipTrigger>
            {!canDeleteTask && (
              <TooltipContent side="top">
                Only user-created tasks can be deleted within 5 minutes of
                creation. Use Closed or N/A instead.
              </TooltipContent>
            )}
          </Tooltip>
          {/* Debug: Generate AI instruction */}
          {matterId && task?.id && (
            <TaskInstructionDebugButton
              matterId={matterId}
              taskId={task.id}
            />
          )}
        </div>
      </TaskSection>
    </>
  );

  const researchAlternateContactsButton = (() => {
    if (task.type?.canonical_type !== CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS) return null;
    const coverageId =
      (task.coverage?.id as string | undefined) ||
      (task.coverage_id as string | undefined);
    if (!matterId || !coverageId) return null;
    return (
      <Button
        variant="outline"
        size="default"
        className="w-full"
        onClick={() =>
          navigate(
            buildClaimsFilingUrl(matterId, coverageId, 'lor'),
          )
        }
      >
        <Send className="h-4 w-4 mr-2" />
        Generate Send LOR
      </Button>
    );
  })();

  const taskDetailContent = isModal ? (
    // Modal layout - structured for drawer with fixed header
    <div className="h-full flex flex-col">
      {/* Header Section - No dividers, cleaner */}
      <div className="px-4 border-b border-slate-100">
        <TaskHeader
          task={taskWithResolvedCoverage || task}
          onTitleChange={(title) => {
            if (!matterId) {
              return;
            }
            updateTask({
              matterId,
              taskId,
              task: { name: title },
            });
          }}
          onStatusChange={handleStatusChange}
          hideStatusControls={hideStatusControls || isMedicalProviderRelatedTask}
          hideTreatmentRequestBadge={isMedicalProviderRelatedTask}
          metadataSlot={metadataSlot}
          instructionSlot={instructionSlot}
          noticeSlot={noticeSlot}
          actionSlot={actionButton}
        />
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-y-scroll no-scrollbar">
        {isPoliceRelatedTask && (
          <ResizablePanelGroup direction="horizontal" className="h-full">
            {/* Police Overview - Left side (resizable, default 60%) */}
            <ResizablePanel defaultSize={60} minSize={30} maxSize={80}>
              <div className="h-full overflow-y-auto">
                <PoliceOverviewMainContent
                  matterId={matterId!}
                  onReportSelect={handlePoliceReportSelect}
                  onCreateReport={handleCreatePoliceReport}
                />
              </div>
            </ResizablePanel>

            <ResizableHandle
              withHandle
              className="bg-slate-200 hover:bg-slate-300 transition-colors"
            />

            {/* Task Detail - Right side (resizable, default 40%) */}
            <ResizablePanel defaultSize={40} minSize={20} maxSize={70}>
              <div className="h-full overflow-y-auto">
                <div className="space-y-0">
                  {/* Task detail sections for police tasks */}
                  {renderTaskDetailSections()}
                </div>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        )}

        {!isPoliceRelatedTask && (
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-0">
              {renderTaskDetailSections()}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showVoiceAIModal && (
        <VoiceAICallModal
          task={taskWithResolvedCoverage || task}
          onClose={() => setShowVoiceAIModal(false)}
          handleSubmit={performTaskAction}
        />
      )}

      {showOperatorMessageModal && (
        <OperatorMessageModal
          task={taskWithResolvedCoverage || task}
          onClose={() => {
            setShowOperatorMessageModal(false);
            refetchTaskUpdates();
          }}
          handleSubmit={() => {
            // Backend handles the update
          }}
        />
      )}

      {showInitialReviewModal && matterId && (
        <InitialReviewModal
          open={showInitialReviewModal}
          onOpenChange={(open) => {
            setShowInitialReviewModal(open);
            if (!open) {
              // Refresh task data after modal closes
              refetchTaskUpdates();
            }
          }}
          matterId={matterId}
          taskId={taskId}
        />
      )}

      {/* Action modal consolidating all task-type-specific actions */}
      <TaskActionModal
        open={actionModalOpen}
        onOpenChange={setActionModalOpen}
        task={taskWithResolvedCoverage || task}
        matterId={matterId}
        coverages={coverages as CoverageSchema[] | undefined}
        onStartInitialReview={handleStartInitialReview}
        onOpenReviewGroupCoverages={() => {
          setActionModalOpen(false);
          setIsReviewGroupCoveragesOpen(true);
        }}
        onOpenVerify1PCoverage={() => {
          setActionModalOpen(false);
          setIsVerify1POpen(true);
        }}
        onLinkCoverageRequest={handleRequestLinkCoverage}
        isLinkCoverageDisabled={
          isCoveragesLoading ||
          isUpdatingCoverageLink ||
          coverageOptions.length === 0
        }
      />

      {/* Review Group Coverages Modal */}
      {isReviewGroupCoveragesOpen && matterId && (
        <ReviewGroupCoveragesModal
          isOpen={isReviewGroupCoveragesOpen}
          onClose={() => setIsReviewGroupCoveragesOpen(false)}
          matterId={matterId}
        />
      )}

      {/* Verify 1P Coverage Modal */}
      {isVerify1POpen &&
        matterId &&
        createPortal(
          <Verify1PCoverageModal
            isOpen={isVerify1POpen}
            onClose={() => setIsVerify1POpen(false)}
            matterId={matterId}
            taskId={taskId}
          />,
          document.body,
        )}

      {isRequestModalOpen && requestIdFromUrl && providerIdFromUrl && (
        <MatterProvider matterId={matterId!}>
          <RequestPageModal
            isOpen={isRequestModalOpen}
            setIsOpen={(open) => {
              if (!open) {
                handleCloseRequestModal();
              }
            }}
            requestId={requestIdFromUrl}
            providerId={providerIdFromUrl}
            highZIndex
          />
        </MatterProvider>
      )}

      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the task &ldquo;{task.name}&rdquo;.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTask}
              disabled={isDeleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  ) : (
    // Non-modal layout - simple scrollable content
    <div>
      {/* Header Section */}
      <div className="px-4 py-3 border-b border-slate-100">
        <TaskHeader
          task={taskWithResolvedCoverage || task}
          onTitleChange={(title) =>
            updateTask({
              matterId: matterId!,
              taskId,
              task: { name: title },
            })
          }
          onStatusChange={handleStatusChange}
          hideStatusControls={hideStatusControls || isMedicalProviderRelatedTask}
          hideTreatmentRequestBadge={isMedicalProviderRelatedTask}
          metadataSlot={metadataSlot}
          instructionSlot={instructionSlot}
          noticeSlot={noticeSlot}
          actionSlot={actionButton}
        />
      </div>

      {/* Content sections */}
      <div className="space-y-0">
        {renderTaskDetailSections()}
      </div>

      {isRequestModalOpen && requestIdFromUrl && providerIdFromUrl && (
        <MatterProvider matterId={matterId!}>
          <RequestPageModal
            isOpen={isRequestModalOpen}
            setIsOpen={(open) => {
              if (!open) {
                handleCloseRequestModal();
              }
            }}
            requestId={requestIdFromUrl}
            providerId={providerIdFromUrl}
            highZIndex
          />
        </MatterProvider>
      )}

      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the task &ldquo;{task.name}&rdquo;.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTask}
              disabled={isDeleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showInitialReviewModal && matterId && (
        <InitialReviewModal
          open={showInitialReviewModal}
          onOpenChange={(open) => {
            setShowInitialReviewModal(open);
            if (!open) {
              refetchTaskUpdates();
            }
          }}
          matterId={matterId}
          taskId={taskId}
        />
      )}

      {/* Action modal (panel view) */}
      <TaskActionModal
        open={actionModalOpen}
        onOpenChange={setActionModalOpen}
        task={taskWithResolvedCoverage || task}
        matterId={matterId}
        coverages={coverages as CoverageSchema[] | undefined}
        onStartInitialReview={handleStartInitialReview}
        onOpenReviewGroupCoverages={() => {
          setActionModalOpen(false);
          setIsReviewGroupCoveragesOpen(true);
        }}
        onOpenVerify1PCoverage={() => {
          setActionModalOpen(false);
          setIsVerify1POpen(true);
        }}
        onLinkCoverageRequest={handleRequestLinkCoverage}
        isLinkCoverageDisabled={
          isCoveragesLoading ||
          isUpdatingCoverageLink ||
          coverageOptions.length === 0
        }
      />

      {isReviewGroupCoveragesOpen && matterId && (
        <ReviewGroupCoveragesModal
          isOpen={isReviewGroupCoveragesOpen}
          onClose={() => setIsReviewGroupCoveragesOpen(false)}
          matterId={matterId}
        />
      )}

      {isVerify1POpen &&
        matterId &&
        createPortal(
          <Verify1PCoverageModal
            isOpen={isVerify1POpen}
            onClose={() => setIsVerify1POpen(false)}
            matterId={matterId}
            taskId={taskId}
          />,
          document.body,
        )}
    </div>
  );

  if (isModal) {
    return (
      <Dialog
        open
        onOpenChange={(open) => {
          // Don't close while waiting for a field change reason modal
          // This prevents a race condition where HeadlessUI dropdown close events
          // trigger the Radix Dialog to close before the modal can appear
          if (!open && isReasonModalPending) {
            return;
          }
          if (!open) {
            onClose();
            // Only navigate if we're in route-based mode (task_id in URL params)
            // If parent is controlling via React state, they handle cleanup via onClose
            const isRouteMode = Boolean(params.task_id);
            if (isRouteMode) {
              // Legacy route mode: /tasks/:task_id - need to navigate to clear it
              navigate(urlMatterId ? `/matters/${matterId}` : '/tasks');
            }
            // else: Parent-controlled mode - parent handles state via onClose callback
          }
        }}
        modal={false}
      >
        <NonModalRightDialogContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            // Prevent dialog from closing when clicking inside the external task linking panel
            // The panel is rendered via createPortal to document.body, which Radix sees as "outside"
            const floatingEl = refs.floating.current;
            if (floatingEl?.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
          className={`bg-white overflow-y-auto overflow-x-visible flex flex-col ${taskDrawerWidthClass} ${
            container ? 'h-[calc(100%-2rem)]' : 'h-[95vh]'
          }`}
        >
          <DialogTitle className="sr-only">Task Details</DialogTitle>
          {taskDetailContent}
        </NonModalRightDialogContent>

        {/* Floating external task linking panel */}
        {isLinkingExternalTasks &&
          matterId &&
          createPortal(
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              role="dialog"
              aria-label="Link External Tasks"
              className="z-[9999] w-[400px] bg-white border border-slate-300 rounded-lg shadow-xl overflow-hidden"
            >
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                <h3 className="font-semibold text-sm text-slate-900">
                  Link External Tasks
                </h3>
                <button
                  type="button"
                  onClick={() => setIsLinkingExternalTasks(false)}
                  className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-200 rounded transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[500px]">
                <ExternalTaskLinkingInterface
                  matterId={matterId}
                  linkedExternalTaskIds={linkedExternalTasks.map((t) => t.id)}
                  onLink={handleLinkExternalTasks}
                  isLinking={isLinkingExternalTask}
                />
              </div>
            </div>,
            document.body,
          )}

        {/* Task CSAT Survey */}
        <TaskCSATSurvey
          open={showCSATSurvey}
          onOpenChange={setShowCSATSurvey}
        />

        {/* TRR Medical Records Upload Modal */}
        {matterId && (
          <DocumentSelectionModal
            open={trrAttachmentModalOpen}
            onClose={() => {
              setTrrAttachmentModalOpen(false);
              pendingTrrStatusRef.current = null;
            }}
            matterId={matterId}
            onSelectDocuments={handleTrrAttachmentSelect}
            onUpload={handleTrrUpload}
            description="Upload Record Set: To mark this task as complete, please upload or select the record set belonging to this task."
            requireSelection
            onBypass={handleTrrBypass}
          />
        )}
      </Dialog>
    );
  }

  // Non-modal view for drawer
  return (
    <>
      <div
        className={`h-full overflow-y-auto bg-white relative ${isDraggingOver ? 'ring-2 ring-inset ring-blue-400' : ''}`}
        onDragEnter={handlePanelDragEnter}
        onDragLeave={handlePanelDragLeave}
        onDragOver={handlePanelDragOver}
        onDrop={handlePanelDrop}
      >
        {/* Drag overlay */}
        {isDraggingOver && (
          <div className="absolute inset-0 z-50 bg-blue-50/80 backdrop-blur-sm flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-blue-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-blue-700">Drop files to attach to this task</p>
              <p className="text-xs font-medium text-blue-500 mt-1">Files will be uploaded and attached automatically</p>
            </div>
          </div>
        )}
        {taskDetailContent}
      </div>

      {/* Task CSAT Survey */}
      <TaskCSATSurvey open={showCSATSurvey} onOpenChange={setShowCSATSurvey} />

      {/* Floating external task linking panel for non-modal */}
      {isLinkingExternalTasks &&
        matterId &&
        createPortal(
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            role="dialog"
            aria-label="Link External Tasks"
            className="z-[9999] w-[400px] bg-white border border-slate-300 rounded-lg shadow-xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="font-semibold text-sm text-slate-900">
                Link External Tasks
              </h3>
              <button
                type="button"
                onClick={() => setIsLinkingExternalTasks(false)}
                className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-200 rounded transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[500px]">
              <ExternalTaskLinkingInterface
                matterId={matterId}
                linkedExternalTaskIds={linkedExternalTasks.map((t) => t.id)}
                onLink={handleLinkExternalTasks}
                isLinking={isLinkingExternalTask}
              />
            </div>
          </div>,
          document.body,
        )}
      {/* TRR Medical Records Upload Modal */}
      {matterId && (
        <DocumentSelectionModal
          open={trrAttachmentModalOpen}
          onClose={() => {
            setTrrAttachmentModalOpen(false);
            pendingTrrStatusRef.current = null;
          }}
          matterId={matterId}
          onSelectDocuments={handleTrrAttachmentSelect}
          onUpload={handleTrrUpload}
          description="Upload or select the documents for this task. Files already attached to this task are pre-selected."
          requireSelection
          onBypass={handleTrrBypass}
          initialSelectedBlobIds={task?.attachments?.map((a) => a.id)}
        />
      )}
    </>
  );
};

/**
 * TaskDetail component with integrated FieldChangeReasonProvider.
 *
 * This wrapper ensures the change reason modal is available for status,
 * due date, and other field changes that require user justification.
 * The provider is scoped to just this component rather than the entire app.
 */
export const TaskDetail: React.FC<TaskDetailProps> = (props) => {
  return (
    <FieldChangeReasonProvider>
      <TaskDetailContent {...props} />
    </FieldChangeReasonProvider>
  );
};
